import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { Modal } from '../components/common/Modal'
import { StreakCelebration } from '../components/common/StreakCelebration'
import { StreakLoss } from '../components/common/StreakLoss'
import { GuestLimitModal } from '../components/common/GuestLimitModal'
import { AudioRecorder } from '../components/conversation/AudioRecorder'
import { ConversationDisplay } from '../components/conversation/ConversationDisplay'
import { PronunciationFeedback } from '../components/conversation/PronunciationFeedback'
import { FlashcardGenerationNotification } from '../components/flashcards/FlashcardGenerationNotification'
import { LanguageInput } from '../components/conversation/LanguageInput'
import { useConversation } from '../hooks/useConversation'
import { useUserStore } from '../store/userStore'
import { useConversationStore } from '../store/conversationStore'
import { useLanguageStore } from '../store/languageStore'
import { useLanguage } from '../hooks/useLanguage'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { speechService } from '../services/speech'
import * as api from '../services/api'
import { APIError } from '../services/api'
import type { LanguageCode } from '../services/languageApi.js'
import { detectVoiceCommand, getCommandDescription } from '../services/voiceCommands'
import { readGuestUserId, storeGuestUserId } from '../utils/guestStorage'

const LIMIT_STORAGE_KEY = 'conversation-limit-date'
const GUEST_LIMIT_KEY = 'guest-message-count'
const GUEST_LIMIT_DATE_KEY = 'guest-limit-date'
const GUEST_MAX_MESSAGES = 1

const getTodayKey = () => new Date().toISOString().split('T')[0]
const AUTO_FLASHCARD_FLAG =
  (import.meta as any).env?.VITE_ENABLE_AUTO_FLASHCARD_GENERATION === 'true'

type CompletionSummary = {
  practiceTime: number
  xpEarned: number
  newLevel: number
  leveledUp: boolean
  grammarNotes: string[]
  pronunciationNotes: string[]
}

type PronunciationMode = 'off' | 'romaji' | 'english' | 'hiragana' | 'symbols'

const PRONUNCIATION_UI_CONFIG: Partial<Record<LanguageCode, {
  toggleLabel: string
  options: Array<{ value: PronunciationMode, label: string }>
}>> = {
  ja: {
    toggleLabel: 'Show Pronunciation',
    options: [
      { value: 'romaji', label: 'Romaji' },
      { value: 'english', label: 'English lettering' },
      { value: 'hiragana', label: 'Hiragana' },
      { value: 'symbols', label: 'Japanese symbols' },
    ],
  },
  zh: {
    toggleLabel: 'Show Pinyin',
    options: [
      { value: 'romaji', label: 'Pinyin (Latin)' },
      { value: 'english', label: 'English lettering' },
    ],
  },
  hi: {
    toggleLabel: 'Show Pronunciation',
    options: [
      { value: 'romaji', label: 'Latin script' },
      { value: 'english', label: 'English lettering' },
    ],
  },
}

/**
 * Helpers to map spoken words (possibly mistranscribed) to the latest AI sentence
 */
const stripDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const sanitizeWordForMatch = (word: string): string =>
  stripDiacritics(word)
    .toLowerCase()
    .replace(/[^a-z0-9Ã±]/gi, '')

const toPhonetic = (word: string): string =>
  sanitizeWordForMatch(word)
    .replace(/gue/g, 'ge')
    .replace(/gui/g, 'gi')
    .replace(/que/g, 'ke')
    .replace(/qui/g, 'ki')
    .replace(/qu/g, 'k')
    .replace(/ll/g, 'y')
    .replace(/ch/g, 'ch')
    .replace(/ce|ci/g, 'si')
    .replace(/c/g, 'k')
    .replace(/ge|gi/g, 'hi')
    .replace(/v/g, 'b')
    .replace(/z/g, 's')
    .replace(/h/g, '')

const normalizeInitialSound = (value: string): string => {
  if (!value) return ''
  const char = value[0]
  if (char === 'q' || char === 'c') return 'k'
  if (char === 'v') return 'b'
  return char
}

const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length

  const rows = b.length + 1
  let previous = new Array(rows).fill(0)
  let current = new Array(rows).fill(0)

  for (let j = 0; j < rows; j++) {
    previous[j] = j
  }

  for (let i = 1; i <= a.length; i++) {
    current[0] = i
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        current[j] = previous[j - 1]
      } else {
        current[j] = Math.min(
          previous[j] + 1,
          current[j - 1] + 1,
          previous[j - 1] + 1
        )
      }
    }
    const temp = previous
    previous = current
    current = temp
  }

  return previous[b.length]
}

const scoreCandidateWord = (
  targetNormalized: string,
  candidateNormalized: string,
  targetPhonetic: string,
  candidatePhonetic: string
): number => {
  if (!candidateNormalized) return 0
  if (targetNormalized === candidateNormalized) return 10

  let score = 0

  if (
    candidateNormalized.includes(targetNormalized) ||
    targetNormalized.includes(candidateNormalized)
  ) {
    score += 1
  }

  const firstSoundMatch =
    normalizeInitialSound(targetPhonetic) === normalizeInitialSound(candidatePhonetic)
  if (firstSoundMatch) {
    score += 0.3
  }

  const lastCharTarget = targetPhonetic[targetPhonetic.length - 1]
  const lastCharCandidate = candidatePhonetic[candidatePhonetic.length - 1]
  if (lastCharTarget && lastCharCandidate && lastCharTarget === lastCharCandidate) {
    score += 0.1
  }

  const lengthDiff = Math.abs(targetPhonetic.length - candidatePhonetic.length)
  score += Math.max(0, 1 - lengthDiff / 6) * 0.2

  const distance = levenshteinDistance(targetPhonetic, candidatePhonetic)
  const similarity = Math.max(
    0,
    1 - distance / Math.max(targetPhonetic.length, candidatePhonetic.length, 1)
  )
  score += similarity

  return score
}

const findWordFromSentence = (spokenWord: string, sentence: string): string | null => {
  const targetNormalized = sanitizeWordForMatch(spokenWord)
  const targetPhonetic = toPhonetic(spokenWord)
  if (!targetNormalized || !targetPhonetic || !sentence) {
    return null
  }

  const tokens = sentence
    .split(/[\s,.;:!?Â¿Â¡()"'Â«Â»\[\]{}]+/)
    .map((token) => token.trim())
    .filter(Boolean)

  let bestMatch: { word: string; score: number } | null = null

  for (const token of tokens) {
    const candidateNormalized = sanitizeWordForMatch(token)
    if (!candidateNormalized) continue

    if (candidateNormalized === targetNormalized) {
      return token
    }

    const candidatePhonetic = toPhonetic(token)
    const score = scoreCandidateWord(
      targetNormalized,
      candidateNormalized,
      targetPhonetic,
      candidatePhonetic
    )

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { word: token, score }
    }
  }

  if (bestMatch && bestMatch.score >= 0.5) {
    return bestMatch.word
  }

  return null
}

export function Conversation() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUserStore((state) => state.user)
  const aiPersonality = useUserStore((state) => state.aiPersonality || 'friendly_tutor')
  const { currentLanguage, currentLanguageCode } = useLanguage()
  const pronunciationConfig = currentLanguage?.code
    ? PRONUNCIATION_UI_CONFIG[currentLanguage.code as LanguageCode] || null
    : null
  const setCurrentLanguagePref = useLanguageStore((state) => state.setCurrentLanguage)
  const { isOnline, wasOffline } = useOnlineStatus()

  const [textInput, setTextInput] = useState('')
  const [showEndModal, setShowEndModal] = useState(false)
  const [lastFeedback, setLastFeedback] = useState<{
    text: string
    confidence: number
  } | null>(null)
  const [flashcardCount, setFlashcardCount] = useState<number | null>(null)
  const [sessionTime, setSessionTime] = useState(0)
  const sessionStartTimeRef = useRef<number>(Date.now())
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const autoStartAttemptedRef = useRef(false)
  const [continuousMode, setContinuousMode] = useState(false)
  const [autoStartRecording, setAutoStartRecording] = useState(false)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [dailyLimitActive, setDailyLimitActive] = useState(false)
  const [completionSummary, setCompletionSummary] = useState<CompletionSummary | null>(null)
  const [showStreakCelebration, setShowStreakCelebration] = useState(false)
  const [streakCelebrationData, setStreakCelebrationData] = useState({
    streakCount: 0,
    isNewStreak: false
  })
  const [showStreakLoss, setShowStreakLoss] = useState(false)
  const [streakLossData, setStreakLossData] = useState({
    previousStreak: 0
  })
  const [dailyAIResponses, setDailyAIResponses] = useState(0)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [pronunciationMode, setPronunciationMode] = useState<PronunciationMode>('off')
  useEffect(() => {
    if (!pronunciationConfig && pronunciationMode !== 'off') {
      setPronunciationMode('off')
      return
    }
    if (pronunciationConfig && pronunciationMode !== 'off') {
      const allowedModes = pronunciationConfig.options.map((option) => option.value)
      if (pronunciationConfig.options.length === 0) {
        setPronunciationMode('romaji')
      } else if (!allowedModes.includes(pronunciationMode)) {
        setPronunciationMode(pronunciationConfig.options[0].value)
      }
    }
  }, [pronunciationConfig, pronunciationMode])
  const [commandFeedback, setCommandFeedback] = useState<string | null>(null)
  const [showCommandsHelp, setShowCommandsHelp] = useState(false)
  const lastAIMessageRef = useRef<{ text: string; audioUrl?: string } | null>(null)
  const greetingAudioPlayedRef = useRef(false)
  const [pageReady, setPageReady] = useState(
    typeof document === 'undefined' ? true : document.readyState === 'complete'
  )
  const [greetingDelayReady, setGreetingDelayReady] = useState(false)

  // Guest mode state
  const [guestMessageCount, setGuestMessageCount] = useState(0)
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false)
  const [showGuestEndModal, setShowGuestEndModal] = useState(false)
  const [userStoreHydrated, setUserStoreHydrated] = useState(() =>
    useUserStore.persist?.hasHydrated?.() ?? true
  )
  const [languageStoreHydrated, setLanguageStoreHydrated] = useState(() =>
    useLanguageStore.persist?.hasHydrated?.() ?? true
  )

  const {
    isLoading,
    error,
    currentSession,
    exchanges,
    startSession,
    sendMessage,
    endSession,
    clearError,
  } = useConversation()
  const resetConversationState = useConversationStore((state) => state.reset)
  const isUsageLimitReached = useConversationStore((state) => state.isUsageLimitReached)
  const setUsageLimitReached = useConversationStore((state) => state.setUsageLimitReached)
  const setConversationError = useConversationStore((state) => state.setError)
  const playbackSpeedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5]
  const selectedLanguageFromNav =
    (location.state as { selectedLanguage?: LanguageCode } | null)?.selectedLanguage || null
  const guestUserIdFromNav = (location.state as { guestUserId?: string } | null)?.guestUserId || null
  const [guestUserId, setGuestUserId] = useState<string | null>(() => readGuestUserId())

  useEffect(() => {
    if (guestUserIdFromNav) {
      setGuestUserId(guestUserIdFromNav)
      storeGuestUserId(guestUserIdFromNav)
    }
  }, [guestUserIdFromNav])

  // Get difficulty from user preferences or default beginner
  const difficulty = user?.difficultyLevel || 'beginner'

  // Initialize guest message count from local storage
  useEffect(() => {
    if (!user) {
      const today = getTodayKey()
      const storedDate = localStorage.getItem(GUEST_LIMIT_DATE_KEY)
      const storedCount = parseInt(localStorage.getItem(GUEST_LIMIT_KEY) || '0', 10)

      if (storedDate === today) {
        setGuestMessageCount(storedCount)
        if (storedCount >= GUEST_MAX_MESSAGES) {
          setShowGuestLimitModal(true)
        }
      } else {
        // Reset for new day
        localStorage.setItem(GUEST_LIMIT_DATE_KEY, today)
        localStorage.setItem(GUEST_LIMIT_KEY, '0')
        setGuestMessageCount(0)
      }
    }
  }, [user])

  // Restore limit lock from previous day if needed
  useEffect(() => {
    if (typeof window === 'undefined') return
    const today = getTodayKey()
    const storedDate = window.localStorage.getItem(LIMIT_STORAGE_KEY)

    if (storedDate === today) {
      setDailyLimitActive(true)
      setShowLimitModal(true)
      setUsageLimitReached(true)
    } else if (storedDate && storedDate !== today) {
      window.localStorage.removeItem(LIMIT_STORAGE_KEY)
      setUsageLimitReached(false)
    }
  }, [setUsageLimitReached])

  useEffect(() => {
    if (!isUsageLimitReached) return
    setDailyLimitActive(true)
    setShowLimitModal(true)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LIMIT_STORAGE_KEY, getTodayKey())
    }
  }, [isUsageLimitReached])

  // Apply selected playback rate to AI audio
  useEffect(() => {
    speechService.setPlaybackRate(playbackRate)
  }, [playbackRate])

  // Fetch daily AI responses count on mount and when session ends
  const fetchDailyAIResponses = async () => {
    if (!user?.id) return
    try {
      const summary = await api.getTodaysSummary(user.id)
      setDailyAIResponses(summary.totalAIResponses)
    } catch (error) {
      console.error('Failed to fetch daily AI responses:', error)
    }
  }

  useEffect(() => {
    fetchDailyAIResponses()
  }, [user?.id])

  useEffect(() => {
    return () => {
      resetConversationState()
      setCompletionSummary(null)
      setShowCompletionModal(false)
    }
  }, [resetConversationState])

  useEffect(() => {
    if (typeof window === 'undefined') {
      setPageReady(true)
      return
    }

    let rafId: number | null = null
    let loadTimerId: number | null = null
    let fallbackTimerId: number | null = null

    const markReady = () => setPageReady(true)

    const scheduleReady = (delay = 180) => {
      loadTimerId = window.setTimeout(markReady, delay)
    }

    if (document.readyState === 'complete') {
      rafId = window.requestAnimationFrame(() => scheduleReady())
    } else {
      const handleLoad = () => scheduleReady()
      window.addEventListener('load', handleLoad, { once: true })
      fallbackTimerId = window.setTimeout(markReady, 2000)

      return () => {
        window.removeEventListener('load', handleLoad)
        if (rafId) window.cancelAnimationFrame(rafId)
        if (loadTimerId) window.clearTimeout(loadTimerId)
        if (fallbackTimerId) window.clearTimeout(fallbackTimerId)
      }
    }

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId)
      if (loadTimerId) window.clearTimeout(loadTimerId)
      if (fallbackTimerId) window.clearTimeout(fallbackTimerId)
    }
  }, [])

  useEffect(() => {
    if (!pageReady) {
      setGreetingDelayReady(false)
      return
    }

    const hasGreeting = exchanges.some((exchange) => exchange.speaker === 'ai' && exchange.audioUrl)
    if (!currentSession || !hasGreeting) {
      setGreetingDelayReady(false)
      return
    }

    setGreetingDelayReady(false)
    const timer = window.setTimeout(() => setGreetingDelayReady(true), 450)
    return () => window.clearTimeout(timer)
  }, [pageReady, currentSession?.id, exchanges])

  useEffect(() => {
    if (selectedLanguageFromNav && selectedLanguageFromNav !== currentLanguageCode) {
      setCurrentLanguagePref(selectedLanguageFromNav)
    }
  }, [selectedLanguageFromNav, currentLanguageCode, setCurrentLanguagePref])

  useEffect(() => {
    const unsub = useUserStore.persist?.onFinishHydration?.(() => setUserStoreHydrated(true))
    return () => unsub?.()
  }, [])

  useEffect(() => {
    const unsub = useLanguageStore.persist?.onFinishHydration?.(() => setLanguageStoreHydrated(true))
    return () => unsub?.()
  }, [])

  useEffect(() => {
    autoStartAttemptedRef.current = false
    greetingAudioPlayedRef.current = false
    setGreetingDelayReady(false)
  }, [user?.id, currentLanguageCode, selectedLanguageFromNav, guestUserId])

  const ensureGuestIdentifier = useCallback(
    async (language: LanguageCode): Promise<string | null> => {
      if (user) return null

      const stored = guestUserId || readGuestUserId()
      if (stored) {
        if (!guestUserId) {
          setGuestUserId(stored)
        }
        return stored
      }

      try {
        const landingPagePath =
          typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/conversation'
        const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined
        const response = await api.logGuestStart({
          languageCode: language,
          landingPagePath,
          referrer,
        })
        if (response?.guestUserId) {
          storeGuestUserId(response.guestUserId)
          setGuestUserId(response.guestUserId)
          return response.guestUserId
        }
      } catch (error) {
        console.error('Failed to allocate guest identifier', error)
      }

      return null
    },
    [guestUserId, user, setGuestUserId]
  )

  useEffect(() => {
    greetingAudioPlayedRef.current = false
    setGreetingDelayReady(false)
  }, [currentSession?.id])

  const ensureConversationSession = useCallback(async () => {
    if (currentSession) {
      return currentSession
    }
    if (isLoading || !userStoreHydrated || !languageStoreHydrated) {
      return null
    }

    const resolvedLanguage: LanguageCode =
      (selectedLanguageFromNav || currentLanguageCode || user?.targetLanguage || 'ja') as LanguageCode

    let resolvedUserId = user?.id || guestUserId
    if (!resolvedUserId) {
      resolvedUserId = await ensureGuestIdentifier(resolvedLanguage)
      if (!resolvedUserId) {
        setConversationError('Unable to start guest session. Please refresh or create a free account.')
        return null
      }
    }

    const response = await startSession(
      resolvedUserId,
      difficulty,
      undefined,
      aiPersonality,
      resolvedLanguage
    )

    return response?.session || null
  }, [
    currentSession,
    isLoading,
    userStoreHydrated,
    languageStoreHydrated,
    user?.id,
    user?.targetLanguage,
    selectedLanguageFromNav,
    currentLanguageCode,
    difficulty,
    aiPersonality,
    startSession,
    guestUserId,
    ensureGuestIdentifier,
    setConversationError,
  ])

  useEffect(() => {
    if (!userStoreHydrated || !languageStoreHydrated) return
    if (currentSession || isLoading || autoStartAttemptedRef.current) return

    autoStartAttemptedRef.current = true
    ensureConversationSession()
  }, [userStoreHydrated, languageStoreHydrated, currentSession, isLoading, ensureConversationSession])

  useEffect(() => {
    if (!currentSession) return
    if (!pageReady || !greetingDelayReady) return
    if (greetingAudioPlayedRef.current) return

    const greeting = exchanges.find((exchange) => exchange.speaker === 'ai' && exchange.audioUrl)
    if (!greeting || !greeting.audioUrl) return

    greetingAudioPlayedRef.current = true
    lastAIMessageRef.current = { text: greeting.textContent, audioUrl: greeting.audioUrl }

      ; (async () => {
        try {
          await new Promise((resolve) => setTimeout(resolve, 200))
          await speechService.playAudio(greeting.audioUrl as string)
        } catch (err) {
          console.error('Failed to play greeting audio:', err)
          greetingAudioPlayedRef.current = false
        }
      })()
  }, [currentSession, exchanges, pageReady, greetingDelayReady])

  // Start timer when session starts
  useEffect(() => {
    if (currentSession && !timerIntervalRef.current) {
      sessionStartTimeRef.current = Date.now()

      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000)
        setSessionTime(elapsed)
      }, 1000)
    }

    // Cleanup timer on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [currentSession])

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Handle voice commands
  const handleVoiceCommand = async (command: ReturnType<typeof detectVoiceCommand>) => {
    try {
      switch (command.type) {
        case 'end_conversation':
          // End conversation directly in hands-free mode (skip confirmation modal)
          if (currentSession) {
            try {
              console.log('[Voice Command] Ending conversation...')

              // Stop continuous mode and recording immediately
              setContinuousMode(false)

              // Stop any audio playback
              speechService.stopPlayback()

              // Stop the timer
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current)
                timerIntervalRef.current = null
              }

              // End the session
              const localDate = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD
              const result = await endSession(currentSession.id, 'completed', localDate)
              console.log('[Voice Command] Session ended, result:', !!result)

              // Check for streak data
              if (result?.streakData?.dailyGoalCompleted) {
                if (result.streakData.streakBroken && result.streakData.previousStreak && result.streakData.previousStreak > 0) {
                  // Show streak loss notification
                  setStreakLossData({
                    previousStreak: result.streakData.previousStreak
                  })
                  setShowStreakLoss(true)
                } else if (result.streakData.streakMaintained) {
                  // Show streak celebration
                  setStreakCelebrationData({
                    streakCount: result.streakData.newStreak,
                    isNewStreak: result.streakData.newStreak === 1
                  })
                  setShowStreakCelebration(true)
                }

                // Update user store with new streak
                if (user) {
                  const { updateUser } = useUserStore.getState()
                  updateUser({ studyStreak: result.streakData.newStreak })
                }
              }

              // Generate flashcards in background if enabled
              if (result && user && AUTO_FLASHCARD_FLAG) {
                generateFlashcards(Number(currentSession.id), Number(user.id))
              }

              // Show completion summary
              if (result) {
                console.log('[Voice Command] Setting completion summary')
                setCompletionSummary({
                  practiceTime: sessionTime,
                  xpEarned: result.xpEarned,
                  newLevel: result.newLevel,
                  leveledUp: result.leveledUp,
                  grammarNotes: result.grammarNotes || [],
                  pronunciationNotes: result.pronunciationNotes || [],
                })
                setShowCompletionModal(true)
                console.log('[Voice Command] Completion modal should now be visible')
              } else {
                console.log('[Voice Command] No result from endSession')
              }
            } catch (err) {
              console.error('[Voice Command] End conversation error:', err)
            }
          }
          break

        case 'translate_word':
          if (command.parameter && user && currentSession) {
            const lastSentence = lastAIMessageRef.current?.text || ''
            const contextualWord = lastSentence
              ? findWordFromSentence(command.parameter, lastSentence)
              : null
            const targetWord = contextualWord || command.parameter

            console.log('[Voice Command] Translating word:', {
              requested: command.parameter,
              resolved: targetWord,
              usedContext: !!contextualWord,
            })

            // Quick fallback dictionary for common words if API fails
            const commonTranslations: Record<string, string> = {
              'qué': 'what',
              'que': 'what/that',
              'como': 'like/as',
              'cómo': 'how',
              'donde': 'where',
              'dónde': 'where',
              'cuando': 'when',
              'cuándo': 'when',
              'porque': 'because',
              'por qué': 'why',
              'quien': 'who',
              'quién': 'who',
            }

            // Try API first
            const translation = await api.translateWordAPI({
              word: targetWord,
              userId: Number(user.id),
              sessionId: String(currentSession.id),
              targetLanguage: currentLanguageCode || 'ja',
              context: lastSentence || undefined,
            })

            console.log('[Voice Command] Translation result:', translation)

            // Use API translation or fallback to dictionary
            let translationText = translation.translation
            if (!translationText || translationText === '[Unavailable]') {
              translationText = commonTranslations[targetWord.toLowerCase()]
            }

            // Speak the translation
            if (translationText && translationText !== '[Unavailable]') {
              const message = `${targetWord} means ${translationText}`
              await speechService.speak(message, 'en-US')
            } else {
              // Fallback if no translation available
              await speechService.speak(`Sorry, I couldn't translate ${targetWord}`, 'en-US')
            }
          }
          break

        case 'repeat_sentence':
          // Replay the last AI message
          if (lastAIMessageRef.current?.audioUrl) {
            await speechService.playAudio(lastAIMessageRef.current.audioUrl)
          }
          break

        case 'translate_sentence':
          // Translate the last AI message to English
          if (lastAIMessageRef.current?.text) {
            const message = `The sentence means: ${lastAIMessageRef.current.text}`
            await speechService.speak(message, 'en-US')
          }
          break

        case 'create_flashcard':
          if (command.parameter && user && currentSession) {
            // Create flashcard for the word
            try {
              const translation = await api.translateWordAPI({
                word: command.parameter,
                userId: Number(user.id),
                sessionId: String(currentSession.id),
                targetLanguage: currentLanguageCode || 'ja',
              })

              await api.createFlashcard({
                userId: Number(user.id),
                japaneseText: translation.word,
                furigana: translation.furigana || '',
                englishTranslation: translation.translation,
                exampleSentence: translation.exampleSentence || '',
                exampleTranslation: translation.exampleTranslation || '',
              })

              // Confirm creation
              await speechService.speak(
                `Flashcard created for ${command.parameter}`,
                'en-US'
              )
            } catch (err) {
              console.error('Failed to create flashcard:', err)
              await speechService.speak('Failed to create flashcard', 'en-US')
            }
          }
          break

        case 'set_speed':
          if (command.parameter) {
            let newRate = playbackRate
            let message = ''

            if (command.parameter === 'slower') {
              // Decrease speed by one step
              const currentIndex = playbackSpeedOptions.indexOf(playbackRate)
              if (currentIndex > 0) {
                newRate = playbackSpeedOptions[currentIndex - 1]
                message = 'Speaking slower'
              } else {
                message = 'Already at minimum speed'
              }
            } else if (command.parameter === 'faster') {
              // Increase speed by one step
              const currentIndex = playbackSpeedOptions.indexOf(playbackRate)
              if (currentIndex < playbackSpeedOptions.length - 1) {
                newRate = playbackSpeedOptions[currentIndex + 1]
                message = 'Speaking faster'
              } else {
                message = 'Already at maximum speed'
              }
            } else if (command.parameter === 'normal') {
              newRate = 1.0
              message = 'Speed set to normal'
            }

            setPlaybackRate(newRate)
            await speechService.speak(message, 'en-US')
          }
          break

        default:
          break
      }
    } catch (err) {
      console.error('[Voice Command] Error:', err)
    }
  }

  const handleRecordingComplete = async (blob: Blob, duration: number) => {
    if (dailyLimitActive) return

    let activeSession = currentSession
    if (!activeSession) {
      activeSession = await ensureConversationSession()
      if (!activeSession) return
    }

    // Check guest limit
    if (!user && guestMessageCount >= GUEST_MAX_MESSAGES) {
      setShowGuestLimitModal(true)
      return
    }

    const result = await sendMessage(activeSession.id, blob, undefined, aiPersonality)

    if (result) {
      // Increment daily AI responses count
      setDailyAIResponses(prev => prev + 1)

      // Update guest limit
      if (!user) {
        const newCount = guestMessageCount + 1
        setGuestMessageCount(newCount)
        localStorage.setItem(GUEST_LIMIT_KEY, newCount.toString())
        if (newCount >= GUEST_MAX_MESSAGES) {
          setShowGuestLimitModal(true)
        }
      }

      const transcribedText = result.userExchange.text

      // Check for voice commands in hands-free mode
      if (continuousMode && transcribedText) {
        const command = detectVoiceCommand(transcribedText)

        if (command.type !== 'none') {
          console.log('[Voice Command] Detected:', command.type, command.parameter)
          setCommandFeedback(getCommandDescription(command))

          // Execute voice command
          await handleVoiceCommand(command)

          // Clear command feedback after 3 seconds
          setTimeout(() => setCommandFeedback(null), 3000)
          return // Don't process as normal conversation
        }
      }

      // Store last AI message for voice commands
      lastAIMessageRef.current = {
        text: result.aiExchange.text,
        audioUrl: result.aiExchange.audioUrl,
      }

      // Show pronunciation feedback if available
      if (result.userExchange.pronunciationFeedback) {
        setLastFeedback({
          text: result.userExchange.pronunciationFeedback,
          confidence: result.userExchange.confidenceScore,
        })
      }

      // Play AI response audio
      if (result.aiExchange.audioUrl) {
        try {
          await speechService.playAudio(result.aiExchange.audioUrl)
        } catch (err) {
          console.error('Failed to play AI audio:', err)
        }
      }

      // Auto-restart recording if in continuous mode
      if (continuousMode) {
        setAutoStartRecording(true)
        // Reset flag after a short delay to allow re-triggering
        setTimeout(() => setAutoStartRecording(false), 100)
      }
    }
  }

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!textInput.trim() || dailyLimitActive) return

    let activeSession = currentSession
    if (!activeSession) {
      activeSession = await ensureConversationSession()
      if (!activeSession) return
    }

    // Check guest limit
    if (!user && guestMessageCount >= GUEST_MAX_MESSAGES) {
      setShowGuestLimitModal(true)
      return
    }

    const result = await sendMessage(activeSession.id, undefined, textInput, aiPersonality)

    if (result) {
      // Increment daily AI responses count
      setDailyAIResponses(prev => prev + 1)

      // Update guest limit
      if (!user) {
        const newCount = guestMessageCount + 1
        setGuestMessageCount(newCount)
        localStorage.setItem(GUEST_LIMIT_KEY, newCount.toString())
        if (newCount >= GUEST_MAX_MESSAGES) {
          setShowGuestLimitModal(true)
        }
      }

      setTextInput('')

      // Play AI response audio
      if (result.aiExchange.audioUrl) {
        try {
          await speechService.playAudio(result.aiExchange.audioUrl)
        } catch (err) {
          console.error('Failed to play AI audio:', err)
        }
      }
    }
  }

  const handleEndConversation = async () => {
    if (!currentSession) return

    // Close modal immediately
    setShowEndModal(false)

    // Stop the timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    // For guest users, show sign-up prompt instead of XP summary
    if (!user) {
      setShowGuestEndModal(true)
      return
    }

    // End the session
    const localDate = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD in local time
    const result = await endSession(currentSession.id, 'completed', localDate)
    console.log('[STREAK DEBUG] Conversation end result:', result)

    // Refresh daily AI responses count
    await fetchDailyAIResponses()

    // Check for streak data in result
    if (result?.streakData?.dailyGoalCompleted) {
      console.log('[STREAK DEBUG] Daily goal completed!', result.streakData)

      if (result.streakData.streakBroken && result.streakData.previousStreak && result.streakData.previousStreak > 0) {
        // Show streak loss notification
        console.log('[STREAK DEBUG] Showing streak loss!', result.streakData.previousStreak)
        setStreakLossData({
          previousStreak: result.streakData.previousStreak
        })
        setShowStreakLoss(true)
      } else if (result.streakData.streakMaintained) {
        // Show streak celebration
        console.log('[STREAK DEBUG] Showing celebration!', result.streakData)
        setStreakCelebrationData({
          streakCount: result.streakData.newStreak,
          isNewStreak: result.streakData.newStreak === 1
        })
        setShowStreakCelebration(true)
      }

      // Update user store with new streak
      if (user) {
        const { updateUser } = useUserStore.getState()
        updateUser({ studyStreak: result.streakData.newStreak })
      }
    } else {
      console.log('[STREAK DEBUG] No streak update. Conditions:', {
        hasResult: !!result,
        hasStreakData: !!result?.streakData,
        dailyGoalCompleted: result?.streakData?.dailyGoalCompleted,
        streakMaintained: result?.streakData?.streakMaintained,
        message: !result?.streakData ? 'Need 5+ AI responses to update streak' : 'Unknown reason'
      })
    }

    // Generate flashcards in the background if enabled
    if (result && user && AUTO_FLASHCARD_FLAG) {
      generateFlashcards(Number(currentSession.id), Number(user.id))
    }

    if (result) {
      setCompletionSummary({
        practiceTime: sessionTime,
        xpEarned: result.xpEarned,
        newLevel: result.newLevel,
        leveledUp: result.leveledUp,
        grammarNotes: result.grammarNotes || [],
        pronunciationNotes: result.pronunciationNotes || [],
      })
      setShowCompletionModal(true)
    } else {
      navigate('/')
    }
  }

  const handleCompletionModalClose = () => {
    setShowCompletionModal(false)
    setCompletionSummary(null)
    navigate('/')
  }

  const generateFlashcards = async (sessionId: number, userId: number) => {
    if (!AUTO_FLASHCARD_FLAG) return
    try {
      const data = await api.generateFlashcards(sessionId, userId)
      if (data.count > 0) setFlashcardCount(data.count)
    } catch (err) {
      if (err instanceof APIError && err.statusCode === 501) {
        // Backend endpoint intentionally unimplemented; ignore quietly
        return
      }
      console.error('Failed to generate flashcards:', err)
    }
  }

  if (!currentSession && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin" />
          <p className="text-lg font-medium text-[var(--color-text)]">Loading conversation...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell fixed inset-0 flex flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <GuestLimitModal
        isOpen={showGuestLimitModal}
        onClose={() => setShowGuestLimitModal(false)}
      />

      {/* Header */}
      <div className="px-4 py-3 flex-shrink-0 shadow-sm" style={{ backgroundColor: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="container mx-auto">
          {/* Mobile: Stack vertically */}
          <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div>
              <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
                Conversation Practice
              </h1>
              <div className="flex items-center gap-3">
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  Level: {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </p>
                {user ? (
                  currentSession && (
                    <p className="text-xs px-2 py-1 rounded-full" style={{
                      backgroundColor: dailyAIResponses >= 5 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                      color: dailyAIResponses >= 5 ? '#16a34a' : '#3b82f6'
                    }}>
                      {dailyAIResponses}/5 AI responses to reach streak
                    </p>
                  )
                ) : (
                  <p className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    Guest Mode: {Math.max(0, GUEST_MAX_MESSAGES - guestMessageCount)} message left
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between md:space-x-4">
              {/* Session Timer */}
              <div className="flex items-center space-x-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-card)' }}>
                <svg className="w-5 h-5" style={{ color: 'var(--color-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-lg font-mono font-semibold" style={{ color: 'var(--color-text)' }}>
                  {formatTime(sessionTime)}
                </span>
              </div>

              {/* Playback speed */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium hidden sm:inline" style={{ color: 'var(--color-text)' }}>AI speed</span>
                <select
                  value={playbackRate}
                  onChange={(e) => setPlaybackRate(Number(e.target.value))}
                  className="rounded-md px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  style={{
                    backgroundColor: 'var(--color-input)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  aria-label="AI playback speed"
                >
                  {playbackSpeedOptions.map((rate) => (
                    <option key={rate} value={rate}>
                      {rate}x
                    </option>
                  ))}
                </select>
              </div>

              <Button variant="error" onClick={() => setShowEndModal(true)} className="whitespace-nowrap">
                End
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Network Status Display */}
      {
        !isOnline && (
          <div className="bg-warning-50 border-b border-warning-200 px-4 py-3 flex-shrink-0">
            <div className="container mx-auto flex items-center justify-center">
              <svg className="w-5 h-5 text-warning-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-warning-700 font-medium text-sm">
                You are offline. Some features may not work.
              </span>
            </div>
          </div>
        )
      }

      {
        wasOffline && isOnline && (
          <div className="bg-success-50 border-b border-success-200 px-4 py-3 flex-shrink-0">
            <div className="container mx-auto flex items-center justify-center">
              <svg className="w-5 h-5 text-success-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-success-700 font-medium text-sm">
                Back online
              </span>
            </div>
          </div>
        )
      }

      {/* Error Display */}
      {
        error && (
          <div className="bg-error-50 border-b border-error-200 px-4 py-3 flex-shrink-0">
            <div className="container mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <span className="text-error-700 text-sm">{error}</span>
                {(error.includes('limit') || error.includes('Premium')) && (
                  <Button
                    variant="primary"
                    onClick={() => navigate('/pricing')}
                    className="text-sm"
                  >
                    Upgrade Now
                  </Button>
                )}
              </div>
              <button
                onClick={clearError}
                className="text-error-500 hover:text-error-700 text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        )
      }

      {/* Pronunciation Feedback */}
      {
        lastFeedback && (
          <div className="px-4 py-3 flex-shrink-0">
            <div className="container mx-auto">
              <PronunciationFeedback
                feedback={lastFeedback.text}
                confidence={lastFeedback.confidence}
                onClose={() => setLastFeedback(null)}
              />
            </div>
          </div>
        )
      }

      {/* Voice Command Feedback */}
      {
        commandFeedback && (
          <div className="px-4 py-3 flex-shrink-0">
            <div className="container mx-auto">
              <div className="rounded-lg border p-4 bg-primary-50 border-primary-200 text-primary-700 animate-slide-down">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" />
                  </svg>
                  <span className="font-semibold">{commandFeedback}</span>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Conversation Display */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-[var(--color-bg)] via-[var(--color-bg)] to-primary-50/30">
        <div className="container mx-auto h-full max-w-5xl px-4 sm:px-6 py-6">
          <ConversationDisplay
            exchanges={exchanges}
            isWaitingForAI={isLoading}
            difficultyLevel={difficulty}
            pronunciationMode={pronunciationMode}
            setPronunciationMode={setPronunciationMode}
          />
        </div>
      </div>

      {/* Input Area */}
      <div className="px-4 py-6 flex-shrink-0 shadow-lg bg-[var(--color-surface)]/95 border-t border-[var(--color-border)]">
        <div className="container mx-auto max-w-5xl">
          {dailyLimitActive ? (
            <div className="text-center py-6 px-4 border border-yellow-200 bg-yellow-50 rounded-lg">
              <p className="text-lg font-semibold text-yellow-900 mb-2">Daily limit reached</p>
              <p className="text-sm text-yellow-800 mb-4">
                You&apos;ve used all available AI responses for today. Come back tomorrow or upgrade to Premium for unlimited conversations.
              </p>
              <div className="flex justify-center space-x-3">
                <Button variant="primary" onClick={() => navigate('/pricing')}>
                  Upgrade to Premium
                </Button>
                <Button variant="outline" onClick={() => setShowLimitModal(true)}>
                  OK
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Mode Toggles */}
              <div className="flex flex-wrap justify-center gap-2 mb-2">
                {/* Pronunciation toggle for supported languages */}
                {pronunciationConfig && exchanges.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setPronunciationMode((prev) => (prev === 'off' ? 'romaji' : 'off'))
                      }
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm ${pronunciationMode === 'off'
                        ? ''
                        : 'bg-primary-600 text-white'
                        }`}
                      style={pronunciationMode === 'off' ? {
                        backgroundColor: 'var(--color-card)',
                        color: 'var(--color-text)'
                      } : {}}
                      title="Toggle pronunciation helper"
                    >
                      <span className="font-medium">
                        {pronunciationMode === 'off' ? pronunciationConfig.toggleLabel : 'Hide Pronunciation'}
                      </span>
                    </button>

                    {pronunciationMode !== 'off' && pronunciationConfig.options.length > 0 && (
                      <select
                        value={pronunciationMode}
                        onChange={(e) => setPronunciationMode(e.target.value as PronunciationMode)}
                        className="px-2 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        style={{
                          backgroundColor: 'var(--color-input)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                        aria-label="Pronunciation style"
                      >
                        {pronunciationConfig.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Hands-Free Mode Toggle */}
                <button
                  onClick={() => setContinuousMode(!continuousMode)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm ${continuousMode
                    ? 'bg-primary-600 text-white'
                    : ''
                    }`}
                  style={!continuousMode ? {
                    backgroundColor: 'var(--color-card)',
                    color: 'var(--color-text)'
                  } : {}}
                  title="Enable hands-free conversation mode"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <span className="font-medium">
                    {continuousMode ? 'Hands-Free Mode: ON' : 'Hands-Free Mode: OFF'}
                  </span>
                </button>

                {/* Voice Commands Help Button - only show when hands-free is enabled */}
                {continuousMode && (
                  <button
                    onClick={() => setShowCommandsHelp(true)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm"
                    style={{
                      backgroundColor: 'var(--color-card)',
                      color: 'var(--color-text)'
                    }}
                    title="View available voice commands"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Voice Commands</span>
                  </button>
                )}
              </div>

              <form onSubmit={handleTextSubmit} className="flex items-center space-x-2">
                <div className="flex-1">
                  <LanguageInput
                    value={textInput}
                    onChange={setTextInput}
                    onSubmit={() => {
                      if (textInput.trim() && !isLoading && isOnline) {
                        handleTextSubmit(new Event('submit') as any)
                      }
                    }}
                    placeholder={
                      currentLanguage?.code === 'ja'
                        ? "Type in romaji (e.g., 'konnichiwa')..."
                        : currentLanguage?.code === 'es'
                          ? "Type in Spanish (e.g., 'hola')..."
                          : currentLanguage?.code === 'tl'
                            ? "Type in Tagalog (e.g., 'kumusta')..."
                            : `Type in ${currentLanguage?.name || 'target language'}...`
                    }
                    disabled={isLoading || !isOnline}
                    userId={user?.id}
                    sessionId={currentSession?.id}
                  />
                </div>

                <AudioRecorder
                  onRecordingComplete={handleRecordingComplete}
                  disabled={isLoading || !isOnline || dailyLimitActive}
                  continuousMode={continuousMode}
                  autoStartRecording={autoStartRecording}
                  variant="inline"
                />

                <Button
                  type="submit"
                  variant="primary"
                  disabled={!textInput.trim() || isLoading || !isOnline || dailyLimitActive}
                >
                  Send
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* End Session Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title="End Conversation?"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowEndModal(false)}>
              Continue
            </Button>
            <Button variant="error" onClick={handleEndConversation}>
              End Session
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          Are you sure you want to end this conversation? Your progress will be saved and
          you'll earn XP based on your practice time.
        </p>
      </Modal>

      {/* Streak Celebration */}
      <StreakCelebration
        isOpen={showStreakCelebration}
        onClose={() => setShowStreakCelebration(false)}
        streakCount={streakCelebrationData.streakCount}
        isNewStreak={streakCelebrationData.isNewStreak}
      />

      {/* Streak Loss Notification */}
      <StreakLoss
        isOpen={showStreakLoss}
        onClose={() => setShowStreakLoss(false)}
        previousStreak={streakLossData.previousStreak}
      />

      {/* Conversation Complete Modal */}
      <Modal
        isOpen={showCompletionModal && !!completionSummary}
        onClose={handleCompletionModalClose}
        title="Conversation Complete"
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={handleCompletionModalClose}>
              Continue
            </Button>
          </div>
        }
      >
        {completionSummary && (
          <div className="space-y-3 text-gray-800">
            <p className="text-sm text-gray-600">Great job! Here&apos;s your practice summary:</p>
            <div className="rounded-lg bg-gray-50 p-4 text-sm">
              <p className="mb-2 font-semibold text-gray-900">
                Practice Time: {formatTime(completionSummary.practiceTime)}
              </p>
              <p className="text-gray-700">XP Earned: {completionSummary.xpEarned}</p>
              <p className="text-gray-700">New Level: {completionSummary.newLevel}</p>
              {completionSummary.leveledUp && (
                <p className="mt-2 font-medium text-emerald-600">Level up! Keep it going!</p>
              )}
            </div>

            {(completionSummary.grammarNotes.length > 0 || completionSummary.pronunciationNotes.length > 0) && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm space-y-3">
                {completionSummary.grammarNotes.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Grammar tips</h3>
                    <ul className="mt-2 list-disc list-inside space-y-1 text-gray-700">
                      {completionSummary.grammarNotes.map((note, idx) => (
                        <li key={`grammar-${idx}`}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {completionSummary.pronunciationNotes.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Pronunciation notes</h3>
                    <ul className="mt-2 list-disc list-inside space-y-1 text-gray-700">
                      {completionSummary.pronunciationNotes.map((note, idx) => (
                        <li key={`pronunciation-${idx}`}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Guest End Conversation Modal */}
      <Modal
        isOpen={showGuestEndModal}
        onClose={() => {
          setShowGuestEndModal(false)
          navigate('/')
        }}
        title="Thanks for Practicing!"
        footer={
          <div className="flex flex-col sm:flex-row justify-end gap-3 w-full">
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to Home
            </Button>
            <Button variant="primary" onClick={() => navigate('/auth')}>
              Create Free Account
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Great job on your practice session! To unlock the full experience and track your progress, create a free account.
          </p>
          <div className="rounded-lg bg-blue-50 p-4">
            <h3 className="font-semibold text-blue-900 mb-2">With a free account, you can:</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Track your XP and level progress</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Build and maintain learning streaks</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Create and review flashcards</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Access unlimited practice sessions</span>
              </li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Flashcard Generation Notification */}
      {
        flashcardCount !== null && (
          <FlashcardGenerationNotification
            count={flashcardCount}
            onClose={() => setFlashcardCount(null)}
          />
        )
      }

      {/* Daily Limit Modal */}
      <Modal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Daily Limit Reached"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="primary" onClick={() => navigate('/pricing')}>
              Upgrade to Premium
            </Button>
            <Button variant="outline" onClick={() => setShowLimitModal(false)}>
              OK
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          You&apos;ve reached the daily limit of AI responses for your plan. Your microphone and text input will
          unlock tomorrow. Upgrade to Premium for unlimited practice sessions.
        </p>
      </Modal>

      {/* Voice Commands Help Modal */}
      <Modal
        isOpen={showCommandsHelp}
        onClose={() => setShowCommandsHelp(false)}
        title="Voice Commands for Hands-Free Mode"
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setShowCommandsHelp(false)}>
              Got it!
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            When Hands-Free Mode is enabled, start every command with <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">toko</span> and use these voice commands to control your conversation:
          </p>

          <div className="space-y-3">
            <div className="border-l-4 border-primary-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">End Conversation</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko end conversation"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko stop"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Immediately ends your session and shows your practice summary.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">Translate Word</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko translate [word]"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko what does [word] mean"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Hear the English translation of any word (e.g., "translate hola").
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">Repeat Sentence</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko repeat"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko one more time"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Replays the last phrase the AI said in the target language.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">Translate Sentence</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko what did you say"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko english please"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Translates the AI's last sentence to English.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">Create Flashcard</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko make a flashcard of [word]"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko remember [word]"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Creates a flashcard for the specified word (e.g., "make a flashcard of gracias").
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-3 py-2">
              <h3 className="font-semibold text-gray-900">Playback Speed</h3>
              <p className="text-sm text-gray-600 mt-1">
                Say: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko slow down"</span> or <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">"toko speak normal"</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Adjusts the AI's speaking speed.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-xs text-blue-900">
              <strong>Tip:</strong> Commands work even if transcribed in the target language. For example, &quot;end conversation&quot; may be recognized as &quot;and conversaciÃ³n&quot; in Spanish mode and still work!
            </p>
          </div>
        </div>
      </Modal>
    </div >
  )
}

export default Conversation


